import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { loadStripe } from '@stripe/stripe-js';
import api from '../services/api';
import CouponInput from '../components/CouponInput';

// Initialize Stripe with your publishable key (only if key is available)
const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

const Checkout = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [coupon, setCoupon] = useState(null);
  
  const tier = searchParams.get('tier') || 'premium';

  // Tier configuration matching backend
  const tiers = {
    core: { name: 'Core Access', price: '$147', description: '10 Modules + 4 Labs + 4 Capstones' },
    complete: { name: 'Complete Package', price: '$197', description: 'Core + Certificate + Future Updates' },
    pro: { name: 'Pro Bundle', price: '$297', description: 'Complete + Priority Support + Code Review' },
    premium: { name: 'Premium Complete', price: '$497', description: 'Pro + Premium Templates + VIP Access' },
    elite: { name: 'Elite Mentorship', price: '$797', description: 'Premium + 1-on-1 Coaching + Career Strategy' },
  };

  const selectedTier = tiers[tier] || tiers.premium;

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent(`/checkout?tier=${tier}`)}`);
    }
  }, [isAuthenticated, navigate, tier]);

  const handleCheckout = async () => {
    setLoading(true);
    setError(null);

    // Check if Stripe is configured
    if (!stripeKey) {
      setError('Payment system is not configured. Please contact support.');
      setLoading(false);
      return;
    }

    try {
      // Create checkout session on backend with optional coupon
      const payload = { tier };
      if (coupon) {
        // Be defensive about the coupon shape in case
        // different environments have slightly different
        // response structures.
        const couponCode = coupon.code || coupon.coupon?.code;
        console.log('Coupon object:', coupon);
        console.log('Extracted coupon code:', couponCode);
        if (couponCode) {
          payload.coupon_code = couponCode;
        }
      }
      console.log('Checkout payload:', payload);
      
      const response = await api.post('/checkout/create-session', payload);
      const { url, message } = response.data;

      // Handle free purchase (100% discount)
      if (!url && message) {
        // Free purchase completed - redirect to success page
        window.location.href = '/payment/success?free=true';
        return;
      }

      // Redirect to Stripe Checkout
      window.location.href = url;

    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.error || 'Unable to start checkout. Please try again.');
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold text-gray-900 mb-2">
            Complete Your Purchase
          </h1>
          <p className="text-gray-600">
            You're one step away from mastering AI agent systems
          </p>
        </div>

        {/* Checkout Card */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          {/* User Info */}
          <div className="mb-6 pb-6 border-b border-gray-200">
            <p className="text-sm text-gray-600">Purchasing as:</p>
            <p className="text-lg font-semibold text-gray-900">{user?.email}</p>
          </div>

          {/* Selected Tier */}
          <div className="mb-8">
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">
                    {selectedTier.name}
                  </h2>
                  <p className="text-gray-600">{selectedTier.description}</p>
                </div>
                <div className="text-right">
                  {coupon && (
                    <p className="text-sm text-gray-500 line-through mb-1">
                      {selectedTier.price}
                    </p>
                  )}
                  <p className="text-3xl font-extrabold text-blue-600">
                    {coupon ? coupon.final_price : selectedTier.price}
                  </p>
                  <p className="text-sm text-gray-500">one-time payment</p>
                  {coupon && (
                    <p className="text-sm font-semibold text-green-600 mt-1">
                      Save {coupon.discount_text}!
                    </p>
                  )}
                </div>
              </div>

              {/* What's Included */}
              <div className="mt-4 pt-4 border-t border-blue-200">
                <p className="text-sm font-semibold text-gray-700 mb-2">What's included:</p>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>✓ Lifetime access to all course content</li>
                  <li>✓ 10 comprehensive modules</li>
                  <li>✓ 4 hands-on labs</li>
                  <li>✓ 4 portfolio capstone projects</li>
                  {tier !== 'core' && <li>✓ Professional certificate</li>}
                  {tier !== 'core' && <li>✓ All future updates (free)</li>}
                  {(tier === 'pro' || tier === 'premium' || tier === 'elite') && <li>✓ Priority support</li>}
                  {(tier === 'premium' || tier === 'elite') && <li>✓ Premium templates & resources</li>}
                  {tier === 'elite' && <li>✓ 1-on-1 coaching sessions</li>}
                </ul>
              </div>
            </div>
          </div>

          {/* Coupon Input */}
          <div className="mb-8">
            <CouponInput 
              tier={tier} 
              amount={selectedTier.price.replace('$', '')}
              onCouponApplied={setCoupon}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Checkout Button */}
          <button
            onClick={handleCheckout}
            disabled={loading}
            className="w-full bg-blue-600 text-white font-bold py-4 px-6 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Proceed to Secure Checkout
              </>
            )}
          </button>

          {/* Security Info */}
          <div className="mt-4 flex items-center justify-center text-sm text-gray-500">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Secure payment powered by Stripe
          </div>
        </div>

        {/* Money-Back Guarantee */}
        <div className="text-center">
          <div className="inline-flex items-center px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm font-medium text-green-800">
              30-day money-back guarantee • No questions asked
            </span>
          </div>
        </div>

        {/* Change Tier Link */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/#pricing')}
            className="text-sm text-blue-600 hover:text-blue-700 underline"
          >
            ← Change pricing tier
          </button>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
